import React, { useState } from "react";
import { Loader2, Landmark, ShieldCheck, Wrench, Tag } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const CATEGORY_STYLE = {
    preventive: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    detective: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    corrective: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
};

// Module-scoped cache — control definitions are static per session, so a chip
// rendered 50 times across SoD results fetches its detail exactly once.
const controlCache = new Map();

/** Clickable Mitigation-proposal chip: shows `MC-xxx · Title` inline and, on
 * click, an elaborated popover with the full control definition — category,
 * type, description, implementation guidance and the Governance Framework
 * categorization it maps to. */
export function MitigationChip({ suggestion }) {
    const [detail, setDetail] = useState(controlCache.get(suggestion.control_id) || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const onOpenChange = async (open) => {
        if (!open || detail || loading) return;
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get(`/governance/mitigation-controls/${suggestion.control_id}`);
            controlCache.set(suggestion.control_id, data);
            setDetail(data);
        } catch (e) {
            setError(e?.response?.status === 404 ? "Control not found in the Mitigation Library" : "Could not load control detail");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Popover onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <button
                    data-testid={`mitigation-chip-${suggestion.control_id}`}
                    onClick={(e) => e.stopPropagation()}
                    title="Click for the elaborated control & governance framework categorization"
                    className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 hover:bg-emerald-500/20 hover:border-emerald-400/50 transition-colors cursor-pointer"
                >
                    {suggestion.control_id} · {suggestion.title}
                </button>
            </PopoverTrigger>
            <PopoverContent
                data-testid="mitigation-popover"
                onClick={(e) => e.stopPropagation()}
                className="w-[380px] bg-[#0A0E18] border border-emerald-500/25 text-slate-200 p-4 shadow-[0_12px_40px_rgba(0,0,0,0.6)]"
                sideOffset={6}
            >
                {loading && (
                    <div className="flex items-center gap-2 text-xs font-mono text-slate-400 py-4 justify-center">
                        <Loader2 size={13} className="animate-spin" /> Loading control…
                    </div>
                )}
                {error && !loading && <p className="text-xs text-rose-300 font-mono py-2">{error}</p>}
                {detail && !loading && (
                    <div className="space-y-3">
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-[11px] text-emerald-300">{detail.control_id}</span>
                                <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${CATEGORY_STYLE[detail.category] || CATEGORY_STYLE.preventive}`}>
                                    {detail.category}
                                </span>
                                <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-white/5 text-slate-400 border-white/10">
                                    {detail.control_type}
                                </span>
                            </div>
                            <div className="text-sm text-white font-medium mt-1">{detail.title}</div>
                        </div>
                        <p className="text-[11px] text-slate-300 leading-relaxed max-h-36 overflow-y-auto">{detail.description}</p>
                        {detail.implementation_guidance && (
                            <div>
                                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-cyan-400/90 mb-1">
                                    <Wrench size={10} /> Implementation Guidance
                                </div>
                                <p className="text-[11px] text-slate-400 leading-relaxed max-h-28 overflow-y-auto">{detail.implementation_guidance}</p>
                            </div>
                        )}
                        {(detail.frameworks || []).length > 0 && (
                            <div>
                                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-amber-400/90 mb-1">
                                    <Landmark size={10} /> Governance Framework Categorization
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    {detail.frameworks.map((fw) => (
                                        <span key={fw.code} title={`${fw.name} · ${fw.region}`} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/20">
                                            {fw.code}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(detail.tags || []).length > 0 && (
                            <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-white/5">
                                <Tag size={10} className="text-slate-500" />
                                {detail.tags.map((t) => (
                                    <span key={t} className="text-[9px] font-mono text-slate-500">{t}</span>
                                ))}
                            </div>
                        )}
                        <div className="flex items-center gap-1.5 text-[9px] font-mono text-slate-400 pt-1">
                            <ShieldCheck size={10} /> Matched to this risk with score {suggestion.match_score}
                        </div>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
